import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import {
  validateUsername,
  getUsernameInputProps,
} from "../../utils/usernameValidation";
import {
  validateDisplayName,
  getDisplayNameInputProps,
} from "../../utils/displayNameValidation";
import { Map, Plus, Shield, Users, Compass } from "lucide-react";

const DashboardContainer = styled.div<{ $isDarkMode: boolean }>`
  min-height: 100vh;
  background: ${(props) =>
    props.$isDarkMode
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"};
  padding: 2rem;
  position: relative;
  overflow: hidden;

  /* Galaxy Stars Background */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
        1px 1px at 23px 47px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 67px 83px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 134px 29px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 189px 76px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 243px 91px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 298px 34px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 356px 68px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 412px 42px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 478px 87px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 523px 31px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 589px 74px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 634px 58px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 691px 23px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 745px 79px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 812px 45px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 867px 82px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 923px 37px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 978px 71px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 1034px 26px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 1089px 84px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 1145px 49px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 1201px 73px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 1256px 38px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 1312px 81px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 1367px 44px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 1423px 69px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 1478px 33px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 1534px 77px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 1589px 52px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 1645px 86px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 1700px 41px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 1756px 75px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 1811px 28px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 1867px 72px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 1922px 47px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 1978px 83px,
        rgba(255, 255, 255, 0.8),
        transparent
      ),
      radial-gradient(
        1px 1px at 2033px 36px,
        rgba(255, 255, 255, 0.6),
        transparent
      ),
      radial-gradient(
        2px 2px at 2089px 79px,
        rgba(255, 255, 255, 0.7),
        transparent
      ),
      radial-gradient(
        1px 1px at 2144px 54px,
        rgba(255, 255, 255, 0.5),
        transparent
      ),
      radial-gradient(
        2px 2px at 2200px 88px,
        rgba(255, 255, 255, 0.8),
        transparent
      );
    background-repeat: repeat;
    background-size: 2250px 120px;
    animation: twinkle 6s ease-in-out infinite;
    opacity: ${(props) => (props.$isDarkMode ? 0.8 : 0.6)};
    z-index: 1;
  }

  /* Twinkling Animation */
  @keyframes twinkle {
    0%,
    100% {
      opacity: ${(props) => (props.$isDarkMode ? 0.8 : 0.6)};
    }
    50% {
      opacity: ${(props) => (props.$isDarkMode ? 1 : 0.8)};
    }
  }

  /* Shooting Star */
  &::after {
    content: "";
    position: absolute;
    top: -50px;
    left: -50px;
    width: 120px;
    height: 3px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.9),
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.9),
      transparent
    );
    transform: rotate(45deg);
    animation: shootingStar 12s linear infinite;
    opacity: 0;
    z-index: 1;
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
  }

  /* Second Shooting Star */
  .shooting-star-2 {
    position: absolute;
    top: -40px;
    left: -40px;
    width: 100px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.8),
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.8),
      transparent
    );
    transform: rotate(35deg);
    animation: shootingStar2 18s linear infinite;
    opacity: 0;
    z-index: 1;
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
  }

  /* Third Shooting Star */
  .shooting-star-3 {
    position: absolute;
    top: -60px;
    left: -60px;
    width: 140px;
    height: 4px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.7),
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.7),
      transparent
    );
    transform: rotate(55deg);
    animation: shootingStar3 25s linear infinite;
    opacity: 0;
    z-index: 1;
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.9);
  }

  @keyframes shootingStar {
    0% {
      transform: translateX(-120px) translateY(-120px) rotate(45deg);
      opacity: 0;
    }
    5% {
      opacity: 1;
    }
    15% {
      transform: translateX(1400px) translateY(1400px) rotate(45deg);
      opacity: 0;
    }
    100% {
      transform: translateX(1400px) translateY(1400px) rotate(45deg);
      opacity: 0;
    }
  }

  @keyframes shootingStar2 {
    0% {
      transform: translateX(-100px) translateY(-100px) rotate(35deg);
      opacity: 0;
    }
    8% {
      opacity: 1;
    }
    20% {
      transform: translateX(1300px) translateY(1300px) rotate(35deg);
      opacity: 0;
    }
    100% {
      transform: translateX(1300px) translateY(1300px) rotate(35deg);
      opacity: 0;
    }
  }

  @keyframes shootingStar3 {
    0% {
      transform: translateX(-140px) translateY(-140px) rotate(55deg);
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    25% {
      transform: translateX(1500px) translateY(1500px) rotate(55deg);
      opacity: 0;
    }
    100% {
      transform: translateX(1500px) translateY(1500px) rotate(55deg);
      opacity: 0;
    }
  }

  /* Ensure content is above background */
  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const DashboardHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  color: white;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff, #f7fafc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.025em;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.95;
  margin-bottom: 1rem;
  font-weight: 400;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 0.75rem;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-template-rows: repeat(2, 1fr);
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 1rem;
    padding: 0 0.5rem;
  }
`;

const DashboardCard = styled.div<{ $index: number; $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#1e293b" : "white")};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: ${(props) =>
    props.$isDarkMode
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 8px 32px rgba(0, 0, 0, 0.1)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid
    ${(props) =>
      props.$isDarkMode
        ? "rgba(148, 163, 184, 0.2)"
        : "rgba(255, 255, 255, 0.2)"};
  animation: slideInUp 0.6s ease-out ${(props) => props.$index * 0.1}s both;
  display: flex;
  flex-direction: column;
  height: 100%;

  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${(props) =>
      props.$isDarkMode
        ? "0 20px 60px rgba(0, 0, 0, 0.4)"
        : "0 20px 60px rgba(0, 0, 0, 0.15)"};
    border-color: rgba(102, 126, 234, 0.3);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const CardIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 1.6rem;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;

  ${DashboardCard}:hover & {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    margin-bottom: 1.25rem;
    font-size: 1.4rem;
  }
`;

const CardTitle = styled.h3<{ $isDarkMode: boolean }>`
  font-size: 1.4rem;
  margin-bottom: 0.75rem;
  color: ${(props) => (props.$isDarkMode ? "#f1f5f9" : "#2d3748")};
  font-weight: 700;
  letter-spacing: -0.025em;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 0.6rem;
  }
`;

const CardDescription = styled.p<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? "#cbd5e1" : "#4a5568")};
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 0.95rem;
  flex-grow: 1;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1.75rem;
    line-height: 1.5;
  }
`;

const ActionButton = styled.button<{ $isDarkMode: boolean }>`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  position: relative;
  overflow: hidden;
  margin-top: auto;
  box-shadow: ${(props) =>
    props.$isDarkMode
      ? "0 8px 25px rgba(102, 126, 234, 0.3)"
      : "0 8px 25px rgba(102, 126, 234, 0.2)"};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.$isDarkMode
        ? "0 12px 35px rgba(102, 126, 234, 0.5)"
        : "0 8px 25px rgba(102, 126, 234, 0.4)"};
  }

  &:active {
    transform: translateY(0);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    border-radius: 10px;
  }
`;

const TourOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TourModal = styled.div<{ $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#1e293b" : "white")};
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  text-align: center;
  box-shadow: ${(props) =>
    props.$isDarkMode
      ? "0 20px 60px rgba(0, 0, 0, 0.5)"
      : "0 20px 60px rgba(0, 0, 0, 0.3)"};
  border: 1px solid
    ${(props) =>
      props.$isDarkMode ? "rgba(148, 163, 184, 0.2)" : "transparent"};
`;

const TourTitle = styled.h2<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? "#f1f5f9" : "#333")};
  margin-bottom: 1rem;
`;

const TourDescription = styled.p<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? "#cbd5e1" : "#666")};
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const TourProgress = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  gap: 0.5rem;
`;

const ProgressStep = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${(props) =>
    props.$isCompleted ? "#10b981" : props.$isActive ? "#3b82f6" : "#e5e7eb"};
  transition: all 0.3s ease;
  position: relative;

  ${(props) =>
    props.$isCompleted &&
    `
    &::after {
      content: "✓";
      position: absolute;
      top: -2px;
      left: 2px;
      color: white;
      font-size: 8px;
      font-weight: bold;
    }
  `}
`;

const ProgressLabel = styled.span<{ $isDarkMode: boolean }>`
  font-size: 0.875rem;
  color: ${(props) => (props.$isDarkMode ? "#94a3b8" : "#6b7280")};
  margin-left: 1rem;
  font-weight: 500;
`;

const TourButton = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  margin: 0 0.5rem;
`;

const DisplayNameInputSection = styled.div`
  margin: 1.5rem 0;
  text-align: center;
`;

const DisplayNameInput = styled.input<{ $hasError: boolean }>`
  width: 100%;
  max-width: 300px;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.$hasError ? "#dc3545" : "#e1e5e9")};
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

const DisplayNamePreview = styled.div`
  margin-top: 0.5rem;
  font-size: 14px;
  color: #666;
`;

const DisplayNameError = styled.div`
  margin-top: 0.5rem;
  color: #dc3545;
  font-size: 14px;
`;

const UsernameInputSection = styled.div`
  margin: 1.5rem 0;
  text-align: center;
`;

const UsernameInput = styled.input<{ $hasError: boolean }>`
  width: 100%;
  max-width: 300px;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.$hasError ? "#dc3545" : "#e1e5e9")};
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

const UsernamePreview = styled.div`
  margin-top: 0.5rem;
  font-size: 14px;
  color: #666;
`;

const UsernameError = styled.div`
  margin-top: 0.5rem;
  color: #dc3545;
  font-size: 14px;
`;

const Dashboard: React.FC = () => {
  const {
    user,
    signupSuccess,
    showUsernameSelection,
    completeGoogleSignup,
    refreshUser,
  } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourStarted, setTourStarted] = useState(false);

  // Use refs to track tour state that won't be affected by re-renders
  const tourStateRef = useRef({
    currentStep: 0,
    showTour: false,
    tourStarted: false,
  });
  const isCompletingTourRef = useRef(false);

  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");

  useEffect(() => {
    // Don't interfere if tour is being completed
    if (isCompletingTourRef.current) {
      return;
    }

    // Don't interfere if tour has already started and is currently showing
    if (tourStarted && showTour) {
      return;
    }

    // Show tour for first-time users or users who need username selection
    if (user) {
      // Check if user is a first-timer (needs username/display name setup)
      const isFirstTimeUser = showUsernameSelection; // Google user needs setup

      // If user needs setup, clear any previous tour completion status and show tour
      if (isFirstTimeUser) {
        localStorage.removeItem("dashboardTourCompleted");
        // First-time user needs setup - show tour from beginning
        setShowTour(true);
        setCurrentStep(0); // Start from welcome step
        setTourStarted(true);
        tourStateRef.current = {
          currentStep: 0,
          showTour: true,
          tourStarted: true,
        };

        // Pre-fill with generated values if they exist
        if (user.displayName && user.displayName.trim()) {
          setDisplayName(user.displayName);
        }
        if (user.username && user.username.trim()) {
          setUsername(user.username);
        }
      } else if (signupSuccess) {
        // Regular email signup - show tour from beginning
        setShowTour(true);
        setCurrentStep(0);
        setTourStarted(true);
        tourStateRef.current = {
          currentStep: 0,
          showTour: true,
          tourStarted: true,
        };
      } else {
        // User has completed setup - mark tour as completed
        localStorage.setItem("dashboardTourCompleted", "true");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, signupSuccess, showUsernameSelection]);

  // Show loading if no user
  if (!user) {
    return null;
  }

  const completeTour = async () => {
    isCompletingTourRef.current = true;
    setShowTour(false);
    setTourStarted(false);
    tourStateRef.current = {
      currentStep: 0,
      showTour: false,
      tourStarted: false,
    };
    localStorage.setItem("dashboardTourCompleted", "true");

    // Add a longer delay to ensure all state updates and Firestore operations have completed
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Refresh user data to ensure display name and username are up-to-date
    try {
      await refreshUser();
    } catch (error) {
      console.error("Error refreshing user data after tour completion:", error);
    } finally {
      isCompletingTourRef.current = false;
    }
  };

  const handleDisplayNameStep = () => {
    // Use centralized display name validation
    const validation = validateDisplayName(displayName);
    if (!validation.isValid) {
      setDisplayNameError(validation.error || "Invalid display name");
      return;
    }

    setDisplayNameError("");
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    tourStateRef.current.currentStep = nextStep;
  };

  const handleUsernameStep = async () => {
    // Use centralized username validation
    const validation = validateUsername(username);
    if (!validation.isValid) {
      setUsernameError(validation.error || "Invalid username");
      return;
    }

    // Store current tour state to restore on error
    const currentTourState = { currentStep, showTour, tourStarted };

    try {
      await completeGoogleSignup(username.trim(), displayName.trim());
      setUsernameError("");

      // Tour completed successfully - complete the tour
      await completeTour();
    } catch (err: any) {
      setUsernameError(err.message);

      // Restore tour state on error
      setCurrentStep(currentTourState.currentStep);
      setShowTour(currentTourState.showTour);
      setTourStarted(currentTourState.tourStarted);
    }
  };

  const tourSteps = [
    {
      title: "Welcome to NewME! 🎉",
      description:
        "Let's get you set up with your profile so you can start creating amazing maps!",
    },
    {
      title: "Choose Your Display Name 👤",
      description:
        "What would you like to be called? You can modify the suggested name or create your own. This is how other users will see you on the platform.",
      isDisplayNameStep: true,
    },
    {
      title: "Choose Your Username 🏷️",
      description:
        "Now let's set up your username. You can modify the suggested username or create your own. This will be your unique identifier on the platform.",
      isUsernameStep: true,
    },
  ];

  const handleCardClick = (action: string) => {
    switch (action) {
      case "my-maps":
        navigate("/my-maps");
        break;
      case "explore":
        navigate("/explore");
        break;
      case "create-map":
        navigate("/create-map");
        break;
      case "approver-panel":
        navigate("/approver-panel");
        break;
      case "admin-panel":
        navigate("/admin-panel");
        break;
      default:
        break;
    }
  };

  return (
    <DashboardContainer $isDarkMode={isDarkMode}>
      {/* Shooting Star Elements */}
      <div className="shooting-star-2"></div>
      <div className="shooting-star-3"></div>

      <DashboardHeader>
        <WelcomeTitle>Welcome back, {user.displayName}! 👋</WelcomeTitle>
        <WelcomeSubtitle>
          {signupSuccess
            ? "Your account has been created successfully!"
            : "Ready to create some amazing maps?"}
        </WelcomeSubtitle>
      </DashboardHeader>

      <DashboardGrid>
        {/* My Maps Card */}
        <DashboardCard
          onClick={() => handleCardClick("my-maps")}
          $index={0}
          $isDarkMode={isDarkMode}
        >
          <CardIcon
            style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }}
          >
            <Map size={24} />
          </CardIcon>
          <CardTitle $isDarkMode={isDarkMode}>My Maps</CardTitle>
          <CardDescription $isDarkMode={isDarkMode}>
            View and manage all the maps you've created. Track your progress and
            organize your work.
          </CardDescription>
          <ActionButton $isDarkMode={isDarkMode}>View My Maps</ActionButton>
        </DashboardCard>

        {/* Explore Card */}
        <DashboardCard
          onClick={() => handleCardClick("explore")}
          $index={1}
          $isDarkMode={isDarkMode}
        >
          <CardIcon
            style={{ background: "linear-gradient(135deg, #4facfe, #00f2fe)" }}
          >
            <Compass size={24} />
          </CardIcon>
          <CardTitle $isDarkMode={isDarkMode}>Explore</CardTitle>
          <CardDescription $isDarkMode={isDarkMode}>
            Discover amazing maps created by the community. Get inspired and
            learn from others.
          </CardDescription>
          <ActionButton $isDarkMode={isDarkMode}>Start Exploring</ActionButton>
        </DashboardCard>

        {/* Create Map Card */}
        <DashboardCard
          onClick={() => handleCardClick("create-map")}
          $index={2}
          $isDarkMode={isDarkMode}
        >
          <CardIcon
            style={{ background: "linear-gradient(135deg, #43e97b, #38f9d7)" }}
          >
            <Plus size={24} />
          </CardIcon>
          <CardTitle $isDarkMode={isDarkMode}>Create New Map</CardTitle>
          <CardDescription $isDarkMode={isDarkMode}>
            Start building your next masterpiece. Create custom maps with your
            own data and style.
          </CardDescription>
          <ActionButton $isDarkMode={isDarkMode}>Create Map</ActionButton>
        </DashboardCard>

        {/* Approver Panel Card (if user is approver or admin) */}
        {(user.role === "approver" ||
          user.role === "admin" ||
          user.email === "admin@NewME.social") && (
          <DashboardCard
            onClick={() => handleCardClick("approver-panel")}
            $index={3}
            $isDarkMode={isDarkMode}
          >
            <CardIcon
              style={{
                background: "linear-gradient(135deg, #fa709a, #fee140)",
              }}
            >
              <Shield size={24} />
            </CardIcon>
            <CardTitle $isDarkMode={isDarkMode}>Approver Panel</CardTitle>
            <CardDescription $isDarkMode={isDarkMode}>
              Review and approve maps submitted by users. Help maintain quality
              standards.
            </CardDescription>
            <ActionButton $isDarkMode={isDarkMode}>Open Panel</ActionButton>
          </DashboardCard>
        )}

        {/* Admin Panel Card (if user is admin) */}
        {user.role === "admin" && (
          <DashboardCard
            onClick={() => handleCardClick("admin-panel")}
            $index={4}
            $isDarkMode={isDarkMode}
          >
            <CardIcon
              style={{
                background: "linear-gradient(135deg, #ff9a9e, #fecfef)",
              }}
            >
              <Users size={24} />
            </CardIcon>
            <CardTitle $isDarkMode={isDarkMode}>Admin Panel</CardTitle>
            <CardDescription $isDarkMode={isDarkMode}>
              Manage users, system settings, and platform administration. Full
              control over the platform.
            </CardDescription>
            <ActionButton $isDarkMode={isDarkMode}>Admin Panel</ActionButton>
          </DashboardCard>
        )}
      </DashboardGrid>

      {/* First-Time User Tour */}
      {showTour && (
        <TourOverlay>
          <TourModal $isDarkMode={isDarkMode}>
            {/* Progress Indicator */}
            <TourProgress $isDarkMode={isDarkMode}>
              {tourSteps.map((_, index) => (
                <ProgressStep
                  key={index}
                  $isActive={index === currentStep}
                  $isCompleted={index < currentStep}
                />
              ))}
              <ProgressLabel $isDarkMode={isDarkMode}>
                {currentStep + 1} of {tourSteps.length}
              </ProgressLabel>
            </TourProgress>

            <TourTitle $isDarkMode={isDarkMode}>
              {tourSteps[currentStep].title}
            </TourTitle>
            <TourDescription $isDarkMode={isDarkMode}>
              {tourSteps[currentStep].description}
            </TourDescription>

            {/* Display Name Selection Step */}
            {tourSteps[currentStep].isDisplayNameStep && (
              <DisplayNameInputSection>
                <DisplayNameInput
                  type="text"
                  placeholder="Enter your display name (or modify the suggestion)"
                  value={displayName}
                  {...getDisplayNameInputProps()}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    setDisplayNameError("");
                  }}
                  $hasError={!!displayNameError}
                />
                {displayName && (
                  <DisplayNamePreview>
                    {user.displayName === displayName ? (
                      <>
                        We generated a random name:{" "}
                        <strong>{displayName}</strong>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#888",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          💡 You can change it!
                        </span>
                      </>
                    ) : (
                      <>
                        You'll be known as: <strong>{displayName}</strong>
                      </>
                    )}
                  </DisplayNamePreview>
                )}
                {displayNameError && (
                  <DisplayNameError>{displayNameError}</DisplayNameError>
                )}
              </DisplayNameInputSection>
            )}

            {/* Username Selection Step */}
            {tourSteps[currentStep].isUsernameStep && (
              <UsernameInputSection>
                <UsernameInput
                  type="text"
                  placeholder="Enter your username (or modify the suggestion)"
                  value={username}
                  {...getUsernameInputProps()}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameError("");
                  }}
                  $hasError={!!usernameError}
                />
                {username && (
                  <UsernamePreview>
                    {user.username === username ? (
                      <>
                        We generated a random username:{" "}
                        <strong>@{username}</strong>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#888",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          💡 You can change it!
                        </span>
                      </>
                    ) : (
                      <>
                        Your profile will be: <strong>@{username}</strong>
                      </>
                    )}
                  </UsernamePreview>
                )}
                {usernameError && (
                  <UsernameError>{usernameError}</UsernameError>
                )}
              </UsernameInputSection>
            )}

            <div>
              {currentStep < tourSteps.length - 1 ? (
                <>
                  <TourButton
                    onClick={completeTour}
                    style={{ background: "#6c757d" }}
                  >
                    Skip Tour
                  </TourButton>
                  <TourButton
                    onClick={() => {
                      if (tourSteps[currentStep].isDisplayNameStep) {
                        handleDisplayNameStep();
                      } else if (tourSteps[currentStep].isUsernameStep) {
                        handleUsernameStep();
                      } else {
                        // Welcome step - just advance to next step
                        setCurrentStep(currentStep + 1);
                      }
                    }}
                    disabled={
                      (tourSteps[currentStep].isDisplayNameStep &&
                        !displayName.trim()) ||
                      (tourSteps[currentStep].isUsernameStep &&
                        !username.trim())
                    }
                  >
                    {tourSteps[currentStep].isDisplayNameStep ||
                    tourSteps[currentStep].isUsernameStep
                      ? "Continue"
                      : "Next"}
                  </TourButton>
                </>
              ) : (
                <TourButton onClick={handleUsernameStep}>
                  Complete Setup!
                </TourButton>
              )}
            </div>
          </TourModal>
        </TourOverlay>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
